import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApi } from "@/hooks/use-api";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { WizardData } from "./AssessmentWizard";
import { CriteriaTarget } from "@/types/assessment";

interface Step2ContentProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
}

export const Step2Content = ({ data, updateData }: Step2ContentProps) => {
  const { parseInstructions, parseRubric } = useApi();
  const [isUploadingInstructions, setIsUploadingInstructions] = useState(false);
  const [isUploadingRubric, setIsUploadingRubric] = useState(false);

  const handleInstructionsFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setIsUploadingInstructions(true);
      try {
        const text = await parseInstructions(file);
        updateData({
          instructions: { markdown: text.markdown, fileType: "pdf" },
          instructionsFileName: file.name,
        });
      } catch (err) {
        console.error("Failed to parse instructions", err);
      } finally {
        setIsUploadingInstructions(false);
      }
    }
  };

  const handleRubricFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setIsUploadingRubric(true);
      try {
        const rubric = await parseRubric(file);
        // Initialize targets if hybrid
        // Let's set default based on prompt logic or just default to document
        const initializedRubric = {
          ...rubric,
          criteria: rubric.criteria.map((c) => ({
            ...c,
            target: "document" as CriteriaTarget,
          })),
        };

        updateData({
          rubric: initializedRubric,
          rubricFileName: file.name,
        });
      } catch (err) {
        console.error("Failed to parse rubric", err);
      } finally {
        setIsUploadingRubric(false);
      }
    }
  };

  const updateCriteriaTarget = (criteriaId: string, target: CriteriaTarget) => {
    if (!data.rubric) return;
    const updatedCriteria = data.rubric.criteria.map((c) =>
      c.id === criteriaId ? { ...c, target } : c
    );
    updateData({
      rubric: { ...data.rubric, criteria: updatedCriteria },
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="instructionsFile">Instructions</Label>
        <div className="relative">
          {data.instructionsFileName ? (
            <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
              <span className="text-sm flex-1 truncate">
                {data.instructionsFileName}
              </span>
              <button
                onClick={() =>
                  updateData({
                    instructions: { markdown: "", fileType: "" },
                    instructionsFileName: null,
                  })
                }
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <Input
                id="instructionsFile"
                type="file"
                accept=".pdf"
                onChange={handleInstructionsFileChange}
                disabled={isUploadingInstructions}
                className="pr-10"
              />
              {isUploadingInstructions && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-zinc-300" />
              )}
            </>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="rubricFile">Grading rubric</Label>
        <div className="relative">
          {data.rubricFileName ? (
            <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
              <span className="text-sm flex-1 truncate">
                {data.rubricFileName}
              </span>
              <button
                onClick={() =>
                  updateData({ rubric: null, rubricFileName: null })
                }
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <Input
                id="rubricFile"
                type="file"
                accept=".pdf"
                onChange={handleRubricFileChange}
                disabled={isUploadingRubric}
                className="pr-10"
              />
            </>
          )}
        </div>
      </div>

      {isUploadingRubric && (
        <div className="border rounded-md p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="font-medium">We’re processing your rubric ...</span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
            Analysing the rubric and extracting the criterion.
          </p>
        </div>
      )}

      {data.rubric && data.type === "hybrid" && (
        <div className="border rounded-md">
          <div className="grid grid-cols-2 gap-4 p-4 border-b bg-muted/50">
            <div className="text-sm font-medium text-muted-foreground">
              Rubric criteria
            </div>
            <div className="text-sm font-medium text-muted-foreground">
              Applies to
            </div>
          </div>
          <div className="divide-y">
            {data.rubric.criteria.map((c) => (
              <div
                key={c.id}
                className="grid grid-cols-2 gap-4 p-4 items-center"
              >
                <div className="text-sm font-medium">{c.criteria}</div>
                <div>
                  <Select
                    value={c.target || "document"}
                    onValueChange={(val) =>
                      updateCriteriaTarget(c.id, val as CriteriaTarget)
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="document">Submission</SelectItem>
                      <SelectItem value="transcript">Transcript</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
